import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import cs from 'classnames';
import { omit } from 'lodash';
import type { FC } from 'react';
import { Children, useEffect, useRef, useState } from 'react';
import type { SwiperRef } from 'swiper/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { SwiperEvents } from 'swiper/types/swiper-events';
import { Button } from '@/DesignSystem/Component';
import type { ISliderProps } from '@/DesignSystem/Model/DataModel';
type IIndex = '-1' | '0' | '1';
const BEFORE_CLASS = "before:absolute before:left-0 before:top-0 before:z-10 before:h-0 before:w-[150px] before:bg-gradient-to-r before:from-blueOne before:ring-0 before:transition-all before:duration-300 before:ease-in-out hover:before:w-0 md:before:content-[''] lg:before:h-[calc(100%-40px)]";
const AFTER_CLASS = "after:absolute after:right-0 after:top-0 after:z-10 after:h-0 after:w-[150px] after:bg-gradient-to-l after:from-blueOne after:ring-0 after:transition-all after:duration-300 after:ease-in-out hover:after:w-0 md:after:content-[''] lg:after:h-[calc(100%-40px)]";
const CLASS_NAMES: Record<IIndex, string> = {
  '-1': BEFORE_CLASS,
  '0': `${BEFORE_CLASS} ${AFTER_CLASS}`,
  '1': AFTER_CLASS
};
const Slider: FC<ISliderProps> = ({
  shadowShow,
  children,
  color,
  pagination,
  navigation,
  id,
  className,
  ...props
}) => {
  const sliderRef = useRef<SwiperRef>(null);
  const [showMoreLayout, setShowMoreLayout] = useState<IIndex>('-1');
  const prevButtonId = `slider__navigation--prev--${id}`;
  const nextButtonId = `slider__navigation--next--${id}`;
  const onSlideChange: SwiperEvents['slideChange'] = swiper => {
    if (swiper.isBeginning) {
      setShowMoreLayout('-1');
    } else if (swiper.isEnd) {
      setShowMoreLayout('1');
    } else {
      setShowMoreLayout('0');
    }
  };
  useEffect(() => {
    if (!sliderRef.current) return () => {};
    sliderRef.current.swiper.on('slideChange', onSlideChange);
    return () => {
      sliderRef.current?.swiper?.off('slideChange', onSlideChange);
    };
  }, [sliderRef]);
  return <div className="relative" data-sentry-component="Slider" data-sentry-source-file="Slider.tsx">
      <Swiper ref={sliderRef} pagination={typeof pagination !== 'object' ? pagination : {
      clickable: true,
      bulletClass: cs(`swiper-pagination-bullet swiper__pagination--color-${color}`, pagination.bulletClass),
      ...omit(pagination, ['bulletClass'])
    }} navigation={typeof navigation !== 'object' ? navigation : {
      prevEl: `#${prevButtonId}`,
      nextEl: `#${nextButtonId}`,
      disabledClass: '!hidden'
    }} className={cs(className, shadowShow ? CLASS_NAMES[showMoreLayout] : '')} {...props} data-sentry-element="Swiper" data-sentry-source-file="Slider.tsx">
        {Children.map(children, child => {
        return <SwiperSlide className="mb-4 pb-6">{child}</SwiperSlide>;
      })}
      </Swiper>
      {navigation ? <div className="hidden lg:block">
          <Button elementId={prevButtonId} className={cs('!absolute right-0 top-[calc(50%-25px)] z-20  h-[50px] w-[50px] !p-0')} color="primary" variant="shadow" radius="circle" />

          <Button elementId={nextButtonId} className="!absolute left-0 top-[calc(50%-25px)] z-20 h-[50px] w-[50px] !p-0" color="primary" variant="shadow" radius="circle" />
        </div> : null}
    </div>;
};
Slider.defaultProps = {
  navigation: {},
  pagination: {},
  shadowShow: true
};
export default Slider;